import React, { useState, useEffect } from "react";
import { Grid, Tab, Tabs, Typography } from "@mui/material";
import { setsConfigAlfaTabs } from "../../utils/constants";
import {
  MAutocomplete,
  MTextField,
  MDatePicker,
  TabPanel,
  ResponsiveFormContainer,
} from "@app/components";
import {
  useEfficiencyParts,
  useEquipmentMide,
  useUser,
} from "@services/servicesGenericsTables";
import { MODES_OPTIONS } from "@app/types/generics.types";
import { useFormContext } from "react-hook-form";
import ChartAlfaAbsorptionForm from "./chartAlfaAbsorption";
import { KeysTabsAnalysis } from "@app/types/samples.types";
import { useAdjustmentCurve } from "../../utils/useAdjustmentCurve";

const LABELS = {
  C: {
    tmpsfcal:
      "Masa del patrón secundario en la f. c. con menor cantidad portador de Fe y Ba (mg)",
    tdpmg:
      "Actividad del patrón secundario en f. c. con menor cantidad portador de Fe y Ba a fecha de contaje (dpm)",
    tef: "Eficiencia de la fuente de calibración con menor cantidad portador de Fe y Ba",
    tcpmps:
      "cpm de la fuente de calibración con menor cantidad portador de Fe y Ba",
    tcal: "Tiempo de contaje de la f. c. con menor cantidad portador de Fe y Ba en total de detectores (min)",
  },
  E: {
    tmpsfcal:
      "Volumen del patrón secundario en la fuente de calibración con espesor cero (ml)",
    tdpmg:
      "Actividad del patrón secundario en la fuente de calibración de espesor cero a fecha de contaje (dpm)",
    tef: "Eficiencia de la fuente de calibración a espesor cero",
    tcpmps: "cpm de la fuente de calibración a espesor cero: U2cpmfc e=0 (K=1)",
    tcal: "Tiempo de contaje de la fuente de calibración a espesor cero en total de detectores (min)",
  },
};

interface AnalysisFormProps {
  mode: string;
}

const AnalysisAlfaTotalForm = ({ mode }: AnalysisFormProps) => {
  const { userList } = useUser();
  const [tabValue, setTabValue] = useState(0);
  const isView = mode === MODES_OPTIONS.VIEW;
  const { equipmentMideListFiltered, loading } = useEquipmentMide();
  const { watch, setValue, getValues } = useFormContext();
  const { efficiencyPartsList } = useEfficiencyParts();
  const listenPart = watch("part");
  const labels = LABELS[listenPart?.parte] || LABELS["C"];
  const { loadingCurve } = useAdjustmentCurve();

  useEffect(() => {
    setValue(
      "part",
      efficiencyPartsList.find((part) => part.parte === "E") || undefined
    );
  }, [efficiencyPartsList]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (getValues("equipment")) return;

    const equipment = equipmentMideListFiltered.find(
      (equipment) => equipment.codigo === "3002" //CODIGO DE EQUIPO DE CENTELLEO
    );
    setValue("equipment", equipment || undefined);
  }, [equipmentMideListFiltered]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <>
      <Tabs
        value={tabValue}
        onChange={handleChange}
        aria-label="Fields uncertainty tabs"
        sx={{ marginBottom: 2 }}
      >
        {setsConfigAlfaTabs.map((set, index) => (
          <Tab key={index} label={set.label} />
        ))}
      </Tabs>
      <TabPanel value={tabValue} index={0} padding={1}>
        <ResponsiveFormContainer columns={4}>
          <MAutocomplete
            name="analyst"
            options={userList}
            label="Seleccione un analista"
            getOptionLabel={(option) => option.username || ""}
            validation={{ required: "El analista es requerido" }}
            disabled={isView}
          />
          <MDatePicker
            name="date"
            pickerType="date"
            label="Fecha calibración"
            validation={{ required: "La fecha es requerida" }}
            disabled={isView}
          />

          <MAutocomplete
            key={JSON.stringify(watch("equipment"))}
            name="equipment"
            options={equipmentMideListFiltered}
            label="Contador Proporcional Alfa-Beta"
            getOptionLabel={(option) =>
              `${option.codigo} - ${option.descripcion}` || ""
            }
            loading={loading}
            disabled={isView}
          />
          <MAutocomplete
            name="part"
            options={efficiencyPartsList.filter(
              (part) => part.analisis === KeysTabsAnalysis.AT
            )}
            label="Parte"
            getOptionLabel={(option) => option.parte || ""}
          />
        </ResponsiveFormContainer>

        <Grid item xs={12} my={2}>
          <Typography variant="tagText">{"Datos patrón primario"}</Typography>
        </Grid>
        <ResponsiveFormContainer columns={2}>
          <MTextField
            name="ta0"
            label="Actividad del patrón primario (Bq/g)"
            placeholder="Actividad del patrón primario (Bq/g)"
            type="number"
            validation={{ required: "La actividad es requerida" }}
            fullWidth
            disabled={isView}
          />
          <MTextField
            name="tua0"
            label="Incertidumbre patrón primario para k=2 (Bq/g)"
            helperText="(*) Aplica los datos del certificado de la fuente patrón"
            placeholder="Incertidumbre patrón primario para k=2 (Bq/g)"
            type="number"
            validation={{ required: "La incertidumbre es requerida" }}
            fullWidth
            disabled={isView}
          />
        </ResponsiveFormContainer>

        <Grid item xs={12} my={2}>
          <Typography variant="tagText">
            {"Datos preparación patrón secundario"}
          </Typography>
        </Grid>
        <ResponsiveFormContainer columns={2}>
          <MTextField
            name="tmpp"
            label="Masa patrón primario (mpp) (mg)"
            placeholder="Masa patrón primario (mpp) (mg)"
            type="number"
            validation={{ required: "La masa es requerida" }}
            fullWidth
            disabled={isView}
          />
          <MTextField
            name="tmps"
            label="Masa patrón secundario (mps) (mg)"
            placeholder="Masa patrón secundario (mps) (mg)"
            type="number"
            validation={{ required: "La masa es requerida" }}
            fullWidth
            disabled={isView}
          />
        </ResponsiveFormContainer>

        <Grid item xs={12} my={2}>
          <Typography variant="tagText">
            {"Datos preparación fuente de calibración"}
          </Typography>
        </Grid>
        <ResponsiveFormContainer columns={2}>
          {Object.entries(labels).map(([name, label]) => (
            <MTextField
              key={name}
              name={name}
              label={label as string}
              placeholder={label as string}
              type="number"
              validation={{ required: "Este campo es requerido" }}
              fullWidth
              disabled={isView}
              step={0.01}
            />
          ))}
          <MTextField
            name="uncertainty_calibration_source_counting_time_without_extinguishing_agent"
            label="Incertidumbre relativa del factor de auto absorción al cuadrado (U2 relfaa)"
            placeholder="Incertidumbre relativa del factor de auto absorción al cuadrado (U2 relfaa)"
            type="number"
            fullWidth
            disabled
          />
        </ResponsiveFormContainer>

        <ResponsiveFormContainer columns={2} mt={2}>
          <MTextField
            name="internal_comments"
            label="Comentarios Internos"
            placeholder="Comentarios Internos"
            type="textArea"
            rows={4}
            multiline
            fullWidth
            disabled={isView}
            step={0.01}
          />
        </ResponsiveFormContainer>
      </TabPanel>
      <TabPanel value={tabValue} index={1} padding={1}>
        {!loadingCurve && <ChartAlfaAbsorptionForm mode={mode} />}
      </TabPanel>
    </>
  );
};

export default AnalysisAlfaTotalForm;
